import { FormattedMessage, useIntl } from 'react-intl';
import { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import Loader from '../../../../common/Loader';
import SectionsSwitch from '../../../../common/SectionSwitch';
import BlockTitle from '../../../layout/BlockTitle';
import { sendRouterSetupOptionsRequest } from '../../../../../utils/api/shortcutApi';
import TextInput from '../../../../Enter/otc/components/TextInput';
import Tooltip from '../../../../common/Tooltip';
import { useOutsideClick } from '../../../../../utils/useOutsideClick';
import CountryPickerList from './CountryPickerList';
import {
  configFilesGroup,
  parseRouterSetupOptions,
  type ConfigGroupType,
  type RouterConfigOptions,
} from './routerConfigUtils';
import { type RouterProtocol, PROTOCOL_OPTIONS } from './RouterProtocolBlock';
import dropdownIcon from '../../../../../../assets/icons/localization/dropdown-arrow.svg';
import premiumIcon from '../../../../../../assets/icons/devices-router/premium.svg';
import generalStyles from '../RouterSettingsSection.scss';
import styles from './RouterConfigBlock.scss';
import itemStyles from './PickerItemComponent.scss';

interface RouterConfigBlockProps {
  hasActiveSubscription: boolean;
  handleConfigChange: (
    shortcutGroupId: string,
    shortcutId: string,
    clusterId: string,
    withEnhancers: boolean,
    isPremiumShortcut: boolean,
  ) => void;
  protocol: RouterProtocol;
  preselectedConfig?: { shortcut_group_id: string; shortcut_id: string; cluster_id: string };
  disabled?: boolean;
}

type ConfigOptionsType = Record<RouterProtocol, RouterConfigOptions>;

export type { ConfigOptions } from './routerConfigUtils';

const searchInputId = 'config-search-input';

const RouterConfigBlock = ({
  hasActiveSubscription,
  handleConfigChange,
  preselectedConfig,
  disabled,
  protocol,
}: RouterConfigBlockProps) => {
  const intl = useIntl();

  const [selectedConfigGroup, setSelectedConfigGroup] = useState<ConfigGroupType>(configFilesGroup.COUNTRIES);
  const [isLoading, setIsLoading] = useState(true);
  const [rawOptions, setRawOptions] = useState<ConfigOptionsType>();
  const [options, setOptions] = useState<RouterConfigOptions>({
    countries: { shortcut_group_id: '', options: [] },
    streaming: { shortcut_group_id: '', options: [] },
  });

  const optimalLocationTitle = intl.formatMessage({
    id: 'profile.routerSection.downloadBlock.optimalLocationLabel',
    defaultMessage: 'Optimal location',
  });

  useEffect(() => {
    sendRouterSetupOptionsRequest()
      .then((response) => {
        const res = {} as ConfigOptionsType;

        PROTOCOL_OPTIONS.forEach((protocolOption) => {
          res[protocolOption] = parseRouterSetupOptions(response, optimalLocationTitle, protocolOption);
        });

        setRawOptions(res);
        setIsLoading(false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (rawOptions) {
      setOptions(rawOptions[protocol]);
    }
  }, [protocol, rawOptions]);

  const [filterInput, setFilterInput] = useState('');

  const [isListShown, setIsListShown] = useState(false);

  const [selectedConfig, setSelectedConfig] = useState({
    shortcut_group_id: '',
    shortcut_id: '',
    cluster_id: '',
  });

  const [selectedViewData, setSelectedViewData] = useState({
    shortcutTitle: '',
    shortcutsAccess: '',
    clusterTitle: '',
    iconUrl: '',
  });
  const [isSelectedViewShown, setIsSelectedViewShown] = useState(false);

  const onSelect = (
    groupId: string,
    shortcutId: string,
    clusterId: string,
    isOptimalLocation: boolean,
    preSelectedConfigGroup?: ConfigGroupType,
  ) => {
    setSelectedConfig({
      shortcut_group_id: groupId,
      shortcut_id: shortcutId,
      cluster_id: clusterId,
    });

    const configGroup = options[preSelectedConfigGroup ?? selectedConfigGroup].options;

    const selectedShortcut = isOptimalLocation
      ? configGroup.find((o) => o.shortcut_id === shortcutId && o.isOptimalLocation === true)
      : configGroup.find((o) => o.shortcut_id === shortcutId && !o.isOptimalLocation);

    if (selectedShortcut) {
      setSelectedViewData({
        shortcutTitle: selectedShortcut.title,
        clusterTitle: selectedShortcut.suboptions.find((s) => s.cluster_id === clusterId)?.title || '',
        iconUrl: selectedShortcut.imageUrl,
        shortcutsAccess: selectedShortcut.shortcuts_access,
      });
      setIsSelectedViewShown(true);
      setFilterInput('');

      handleConfigChange(
        groupId,
        shortcutId,
        clusterId,
        groupId === options.countries.shortcut_group_id,
        selectedShortcut.shortcuts_access === 'SHORTCUTS_ACCESS_PREMIUM',
      );
      setIsListShown(false);
    }
  };

  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!options.countries.shortcut_group_id || !options.streaming.shortcut_group_id) {
      return;
    }

    if (preselectedConfig) {
      const selectedGroup =
        options.streaming.shortcut_group_id === preselectedConfig.shortcut_group_id
          ? configFilesGroup.STREAMING
          : configFilesGroup.COUNTRIES;

      setSelectedConfigGroup(selectedGroup);

      onSelect(
        preselectedConfig.shortcut_group_id,
        preselectedConfig.shortcut_id,
        preselectedConfig.cluster_id,
        false,
        selectedGroup,
      );
    }
  }, [preselectedConfig, options]);

  const formWrapperRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick(formWrapperRef, () => {
    setIsListShown(false);
  });

  return (
    <article>
      <BlockTitle>
        <FormattedMessage
          id="profile.routerSection.downloadBlock.title"
          defaultMessage="Set up the configuration file"
        />
      </BlockTitle>
      <p className={generalStyles.text}>
        <FormattedMessage
          id="profile.routerSection.downloadBlock.subtitle"
          defaultMessage="Select the country or streaming service you want to connect to. We'll download the corresponding .ovpn file to your device."
        />
      </p>

      <div className={styles.searchFormWrapper} ref={formWrapperRef}>
        <TextInput
          hideError
          id={searchInputId}
          value={filterInput}
          ref={searchInputRef}
          disabled={disabled}
          className={clsx(
            styles.searchInput,
            isSelectedViewShown && selectedViewData.shortcutTitle && styles.searchInputSelected,
          )}
          handleChange={(e) => {
            if (selectedViewData.shortcutTitle) {
              setSelectedViewData({ shortcutTitle: '', clusterTitle: '', iconUrl: '', shortcutsAccess: '' });
              setIsSelectedViewShown(false);
            }
            setFilterInput(e.value);
            setIsListShown(true);
          }}
          placeholder={intl.formatMessage({
            id: 'profile.routerSection.downloadBlock.inputPlaceholder',
            defaultMessage: 'Countries and streaming services',
          })}
          leftSideSlot={
            isSelectedViewShown && selectedViewData.shortcutTitle ? (
              <button
                type="button"
                disabled={disabled}
                className={clsx(styles.selectedView, itemStyles.mainLine)}
                onClick={() => {
                  if (!disabled) {
                    searchInputRef.current?.focus();
                  }
                }}
              >
                <div className={itemStyles.title}>
                  <div className={itemStyles.imageWrapper}>
                    <img className={itemStyles.image} src={selectedViewData.iconUrl} width={42} height={32} alt="" />
                    {selectedViewData.shortcutsAccess === 'SHORTCUTS_ACCESS_PREMIUM' && !hasActiveSubscription ? (
                      <img className={itemStyles.premiumIcon} src={premiumIcon} width={20} height={20} alt="" />
                    ) : null}
                  </div>
                  {selectedViewData.shortcutTitle}
                  <span className={itemStyles.titleOpaque}>·</span>
                  <span className={itemStyles.titleOpaque}>{selectedViewData.clusterTitle}</span>
                </div>
              </button>
            ) : null
          }
          rightSideSlot={
            <button
              type="button"
              className={clsx(styles.expandButton, isListShown && styles.expandButtonContract)}
              title={isListShown ? 'Hide options' : 'Show options'}
              disabled={disabled}
              onClick={() => {
                if (disabled) {
                  return;
                }
                setIsListShown((is) => !is);
              }}
            >
              <img src={dropdownIcon} alt="" />
            </button>
          }
          onFocus={() => {
            if (disabled) {
              return;
            }
            setIsSelectedViewShown(false);
            setIsListShown(true);
          }}
          onBlur={() => {
            setIsSelectedViewShown(true);
          }}
        />

        <section
          className={clsx(styles.listWrapper, isListShown && styles.listWrapperShown)}
          aria-hidden={!isListShown}
          data-testid="router-config-list"
        >
          <SectionsSwitch
            options={Object.values(configFilesGroup)}
            selectedOption={selectedConfigGroup}
            name="router-config-files"
            className={styles.configTypeSwitch}
            optionLabels={{
              [configFilesGroup.COUNTRIES]: (
                <FormattedMessage
                  id="profile.routerSection.downloadBlock.configFilesType.countries"
                  defaultMessage="Country list"
                />
              ),
              [configFilesGroup.STREAMING]: (
                <FormattedMessage
                  id="profile.routerSection.downloadBlock.configFilesType.streaming"
                  defaultMessage="Streaming services"
                />
              ),
            }}
            onSelected={setSelectedConfigGroup}
          />
          {isLoading ? (
            <div className={styles.optionsLoaderWrapper}>
              <Loader colored />
            </div>
          ) : (
            <CountryPickerList
              filterValue={filterInput}
              selectedItem={selectedConfig}
              hasActiveSubscription={hasActiveSubscription}
              notFoundMessage={
                selectedConfigGroup === configFilesGroup.COUNTRIES
                  ? intl.formatMessage({
                      id: 'profile.routerSection.downloadBlock.countryNotFound',
                      defaultMessage: 'Sorry, we do not have this country in our list.',
                    })
                  : intl.formatMessage({
                      id: 'profile.routerSection.downloadBlock.streamingNotFound',
                      defaultMessage: 'Sorry, we do not have this streaming service in our list.',
                    })
              }
              options={
                selectedConfigGroup === configFilesGroup.COUNTRIES
                  ? options.countries.options
                  : options.streaming.options
              }
              onSelect={onSelect}
            />
          )}
        </section>
      </div>

      <Tooltip anchorSelect={`#${searchInputId}`} place="bottom-end" hidden={!disabled}>
        <FormattedMessage
          id="profile.routerSection.enhancersBlock.cannotChangeExistingRouterSettings"
          defaultMessage="You can’t change the settings for this device"
        />
      </Tooltip>
    </article>
  );
};

export default RouterConfigBlock;
